import {
  ChangeEvent,
  CSSProperties,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../redux/store";
import { Button } from "../../../components/button/Button";
import { ModalContext } from "../../../components/Modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleXmark } from "@fortawesome/free-solid-svg-icons";
import Checkbox from "../../../components/checkbox/Checkbox";
import {
  ColumnMenuModule,
  ColumnsToolPanelModule,
  ContextMenuModule,
  CellStyleModule,
  FiltersToolPanelModule,
  SetFilterModule,
} from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import {
  ClientSideRowModelModule,
  DateFilterModule,
  ModuleRegistry,
  NumberFilterModule,
  TextFilterModule,
  ValidationModule,
  RowSelectionModule,
  RowSelectionOptions,
} from "ag-grid-community";
import useAgGridTheme from "../../../gridUtils/agGridTheme";
import {
  DEFAULT_COL_DEF,
  DEFAULT_GRID_OPTIONS,
} from "../../../gridUtils/gridUtils";
import {
  defaultFilterSelections,
  FilterBoxProps,
  FilterOption,
  TimeFilterOption,
  TimeCategoryOption,
} from "../../../interfaces/queryBuilderInterfaces";

ModuleRegistry.registerModules([
  RowSelectionModule,
  ClientSideRowModelModule,
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  ColumnMenuModule,
  ContextMenuModule,
  CellStyleModule,
  SetFilterModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  ValidationModule,
]);

const FilterBoxWithoutOptions = ({
  treeData,
  filterSelections,
  applyFilter,
  advancedFilterGridRef,
  advancedFilterGridColumn,
  handleCheckAll,
  handleUncheckAll,
  onRowSelected,
  onCellClicked,
  selectRowsByArray,
  currentSelections,
  setExclude,
  clearSelections,
  selectRowsByCurrentSelections,
}: FilterBoxProps) => {
  const currentOptions = currentSelections.map((item: any) =>
    item.name
      ? {
          name: item.name,
          caption: item.name,
          altCaption: item.name,
          uniqueName: item.name,
          properties: {},
        }
      : {
          name: item,
          caption: item,
          altCaption: item,
          uniqueName: item,
          properties: {},
        },
  );
  const [filterOptions, setFilterOptions] =
    useState<TimeFilterOption[]>(currentOptions);
  const [options, setOptions] = useState<TimeFilterOption[]>(currentOptions);
  const [isShowSelected, setShowSelected] = useState(false);
  const [isLoading, setLoading] = useState(true);
  const [style, setStyle] = useState<CSSProperties>({ height: "220px" });
  const dispatch: AppDispatch = useDispatch();
  const modalContext = useContext(ModalContext);
  const close = modalContext ? modalContext.close : () => {};
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [applyTo, setApplyTo] = useState<string | null>(null);
  const [showError, setShowError] = useState(false);
  const currentSearchText = currentSelections
    .map((item) => item.name)
    .join(",");
  const [searchText, setSearchText] = useState(currentSearchText);
  const [isExclude, setInternalExclude] = useState(filterSelections.exclude);
  const rowSelection = useMemo<
    RowSelectionOptions | "single" | "multiple"
  >(() => {
    return { mode: "multiRow" };
  }, []);

  const excludeHandler = () => {
    if (setExclude) {
      setExclude(!isExclude);
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
      setShowMenu(false);
    }
  };

  const handleShowSelectedInternal = (
    flag: boolean,
    selectedOption1: TimeCategoryOption | undefined,
    selectedOption2: TimeCategoryOption | undefined,
    searchVal: string,
  ) => {
    let filteredOptions = options;
    if (flag && currentSelections) {
      filteredOptions = filteredOptions.filter((item) =>
        currentSelections.some((selectedRow) => selectedRow.name === item.name),
      );
    }
    setFilterOptions(filteredOptions);
  };

  const showSelectedHandler = () => {
    handleShowSelectedInternal(
      !isShowSelected,
      undefined,
      undefined,
      searchText,
    );
    setShowSelected(!isShowSelected);
  };

  useEffect(() => {
    if (
      filterSelections &&
      options &&
      options.length > 0 &&
      selectRowsByArray &&
      currentSelections
    ) {
      let selectedObjects: FilterOption[] = [];
      if (currentSelections) {
        selectedObjects = options.filter((item) =>
          currentSelections.map((i) => i.name).includes(item.name),
        );
      }
      selectRowsByArray(
        selectedObjects.map((item) => ({
          ...item,
          altCaption: item.caption, // Provide a default or derived value for altCaption
          properties: {}, // Provide a default or derived value for properties
        })),
      );
      //setExclude(filterSelections.exclude);
      setApplyTo(filterSelections.applyTo);
      if (treeData.name === "Time Period" && currentSelections.length === 0) {
        setShowError(true);
      } else {
        setShowError(false);
      }
    }
  }, [options, filterOptions, currentSelections]);

  useEffect(() => {
    if (showMenu) {
      document.addEventListener("click", handleClickOutside);
    } else {
      document.removeEventListener("click", handleClickOutside);
    }
  }, [showMenu]);

  const debounceSearch = (event: ChangeEvent<HTMLInputElement>) => {
    handleSearch(event.target.value.toUpperCase());
  };

  const handleSearch = (searchVal: string) => {
    setSearchText(searchVal);
    const uniqueItems = new Set(searchVal.split(","));
    const val = Array.from(uniqueItems);
    const filters: TimeFilterOption[] = val
      .filter((item) => item.trim().length > 0)
      .map((item) => ({
        name: item.trim(),
        caption: item.trim(),
        uniqueName: item.trim(),
        altCaption: item.trim(),
        properties: {},
      }));
    setFilterOptions(filters);
    setOptions(filters);
    setShowSelected(false);
  };

  const onPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    let pastedData = event.clipboardData.getData("text");
    pastedData = pastedData.replace(/\r\n/g, ",");
    pastedData = pastedData.replace(/\n/g, ",");
    if (searchText && searchText.length > 0) {
      pastedData = searchText + pastedData;
    }
    handleSearch(pastedData.toUpperCase());

    event.preventDefault();
  };

  useEffect(() => {
    if (
      filterOptions &&
      advancedFilterGridRef &&
      advancedFilterGridRef.current &&
      advancedFilterGridRef.current.api
    ) {
      if (filterOptions.length === 0 && clearSelections) {
        clearSelections();
      }
      if (selectRowsByCurrentSelections) {
        selectRowsByCurrentSelections();
      }
    }
  }, [filterOptions]);
  return (
    <div className="filter-dialog-container dialog-box-min filter-box">
      <div className="dialog-body nopadding">
        <div>
          <div className="search-filter">
            <input
              type="text"
              className="search-input"
              placeholder="Provide list of values separated by commas"
              onChange={debounceSearch}
              onPaste={onPaste}
              value={searchText}
              style={{
                fontWeight: "normal",
                width: "100%",
                padding: "10px 45px 10px 10px",
                boxSizing: "border-box",
              }}
            />

            {searchText && searchText.length > 0 && (
              <span
                className="search-clear"
                onClick={() => {
                  setSearchText("");
                  setFilterOptions([]);
                }}
                style={{
                  position: "relative",
                  left: "93%", // Use the new prop here
                  top: "-30px",
                  //transform: 'translateY(-50%)',
                  cursor: "pointer",
                  color: "#132445",
                }}
              >
                <FontAwesomeIcon icon={faCircleXmark} />
              </span>
            )}
          </div>
          <div className="filter-options-section">
            <div className="filter-checkbox-section">
              <label className="radio-inline" htmlFor="show-selected-radio">
                <Checkbox
                  id="show-selected-radio"
                  name="show-selected"
                  checked={isShowSelected}
                  onChange={showSelectedHandler}
                  value={""}
                  styleClass={""}
                  color={""}
                />
                <span className="ellipsis">Show Selected</span>
              </label>
              {false && treeData.name !== "Time Period" && (
                <label className="radio-inline" htmlFor="excluded-radio">
                  <Checkbox
                    id="excluded-radio"
                    name="exclude"
                    checked={isExclude}
                    onChange={excludeHandler}
                    value={""}
                    styleClass={""}
                    color={""}
                  />
                  <span className="ellipsis">Exclude</span>
                </label>
              )}
            </div>

            <div className="filter-button-section">
              <Button
                disabled={filterOptions && (filterOptions.length > 999 || filterOptions.length === 0)}
                color="primary"
                className="btn md"
                variant="plain"
                id="btn-apply"
                onClick={handleCheckAll}
              >
                Select All
              </Button>
              <Button
              disabled={filterOptions && (filterOptions.length > 999 || filterOptions.length === 0)}
                color="primary"

                className="btn md"
                variant="plain"
                id="btn-apply"
                onClick={handleUncheckAll}
              >
                Deselect All
              </Button>
            </div>
          </div>

          <div className="ag-theme-alpine grid-wrapper grid-multi-select">
            <div style={style}>
              <AgGridReact<TimeFilterOption>
                theme={useAgGridTheme("compact")}
                ref={advancedFilterGridRef}
                rowData={filterOptions}
                columnDefs={advancedFilterGridColumn}
                defaultColDef={DEFAULT_COL_DEF}
                rowSelection={rowSelection}
                gridOptions={DEFAULT_GRID_OPTIONS}
                onRowSelected={onRowSelected}
                onCellClicked={onCellClicked}
                suppressContextMenu={true}
                headerHeight={0}
                onGridReady={(params) => {
                  if (selectRowsByArray && currentSelections) {
                    selectRowsByArray(currentSelections);
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>
      {showError && (
        <div style={{ fontSize: "12px", color: "red" }}>
          Must select at least one filter value for this field
        </div>
      )}
    </div>
  );
};

export default FilterBoxWithoutOptions;
